import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { getApiUrl, apiFetch } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import './HomePage.css';
import { clickable } from '../../utils/a11y';
import { useTranslation } from 'react-i18next';

const SalesHomePage = () => {
  const { t } = useTranslation('sales');
  const { user } = useAuth();
  const navigate = useNavigate();
  const { openEntity } = useAppNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [recentQuotes, setRecentQuotes] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [metricsRes, quotesRes] = await Promise.all([
        apiFetch(getApiUrl('/api/sales/metrics/overview')),
        apiFetch(getApiUrl('/api/quotes?limit=5'))
      ]);

      if (metricsRes.ok) {
        setMetrics(await metricsRes.json());
      }
      if (quotesRes.ok) {
        const data = await quotesRes.json();
        setRecentQuotes((data.quotes || data || []).slice(0, 5));
      }
    } catch (error) {
      console.error('Error fetching sales data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (val) => {
    const num = parseFloat(val) || 0;
    return `$${num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  return (
    <div className="home-page">
      <div className="page-header">
        <h1>{t('salesHomePage.title')}</h1>
        <p className="welcome-text">{t('salesHomePage.welcome', { name: user?.full_name || user?.username })}</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card" {...clickable(() => navigate('/sales?tab=quotations'))}>
          <div className="stat-content">
            <h3>{t('salesHomePage.quotes30d')}</h3>
            <p className="stat-value">{loading ? '...' : metrics?.quotations?.created_last_30_days ?? 0}</p>
            {!loading && metrics?.quotations && (
              <p className="stat-subtitle">{t('salesHomePage.thisWeek', { count: metrics.quotations.created_last_7_days })}</p>
            )}
          </div>
        </div>

        <div className="stat-card" {...clickable(() => navigate('/sales?tab=quotations'))}>
          <div className="stat-content">
            <h3>{t('salesHomePage.acceptedApproved')}</h3>
            <p className="stat-value">{loading ? '...' : (metrics?.quotations?.accepted ?? 0) + (metrics?.quotations?.approved ?? 0)}</p>
            {!loading && metrics?.conversions && (
              <p className="stat-subtitle">{t('salesHomePage.conversionRate', { rate: metrics.conversions.conversion_rate })}</p>
            )}
          </div>
        </div>

        <div className="stat-card" {...clickable(() => navigate('/sales?tab=quotations'))}>
          <div className="stat-content">
            <h3>{t('salesHomePage.pipelineValue')}</h3>
            <p className="stat-value">{loading ? '...' : formatCurrency(metrics?.revenue?.pending_value)}</p>
            {!loading && (
              <p className="stat-subtitle">{t('salesHomePage.pendingQuotes', { count: metrics?.quotations?.pending ?? 0 })}</p>
            )}
          </div>
        </div>

        <div className="stat-card" {...clickable(() => navigate('/sales'))}>
          <div className="stat-content">
            <h3>{t('salesHomePage.revenue30d')}</h3>
            <p className="stat-value">{loading ? '...' : formatCurrency(metrics?.revenue?.value_last_30_days)}</p>
            {!loading && (
              <p className="stat-subtitle">{t('salesHomePage.acceptedTotal', { amount: formatCurrency(metrics?.revenue?.accepted_value) })}</p>
            )}
          </div>
        </div>
      </div>

      <div className="quick-actions">
        <h2>{t('salesHomePage.quickActions')}</h2>
        <div className="action-buttons">
          <button onClick={() => navigate('/sales')} className="action-btn">
            {t('salesHomePage.actions.viewSalesDashboard')}
          </button>
          <button onClick={() => navigate('/sales?tab=quotations')} className="action-btn">
            {t('salesHomePage.actions.manageQuotes')}
          </button>
        </div>
      </div>

      <div className="recent-activity">
        <h2>{t('salesHomePage.recentQuotes')}</h2>
        {loading ? (
          <p style={{ color: 'var(--text-secondary)' }}>{t('common:ui.loading')}</p>
        ) : recentQuotes.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)' }}>{t('salesHomePage.noRecentQuotes')}</p>
        ) : (
          <div className="activity-list">
            {recentQuotes.map((q) => (
              <div
                key={q.quotation_id || q.id}
                className="inspection-item"
                {...clickable(() => openEntity('quote', q.quotation_id || q.id, 'Dashboard'))}
              >
                <span className={`inspection-badge ${q.status === 'ACCEPTED' || q.status === 'APPROVED' ? 'passed' : q.status === 'REJECTED' ? 'failed' : 'pending'}`}>
                  {q.status}
                </span>
                <span className="inspection-details">
                  {q.quotation_number || q.quote_number} — {q.customer_name || t('common:ui.customer')}
                </span>
                <span className="inspection-time">
                  {formatCurrency(q.total_amount)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesHomePage;
